import React from "react";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import CustomButton from "components/CustomButton";
import { DeleteOutlineRounded, Save } from "@mui/icons-material";
import sessionHelperInstance from "services/sessionHelper";

const useStyles = makeStyles((theme) => ({
  betLimitItemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  betLimitItemPaper: {
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: theme.spacing(1),
    "&.disabled": {
      backgroundColor: "#f5f5f5",
      opacity: 0.7,
    },
    "&.enabled": {
      backgroundColor: "#ffffff",
    },
  },
  betLimitItemFields: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
    },
  },
  field: {
    minWidth: 0,
  },
  smallField: {
    minWidth: 0,
  },
  removeButton: {
    "&.disabled": {
      color: "#ccc",
    },
    "&.enabled": {
      color: "#f44336",
      "&:hover": {
        backgroundColor: "rgba(244, 67, 54, 0.04)",
      },
    },
  },
  betLimitsButtonContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  addBetLimitButton: {
    "&.enabled": {
      backgroundColor: "#4caf50",
      color: "white",
      "&:hover": {
        backgroundColor: "#45a049",
      },
    },
    "&.disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
  removeAllButton: {
    "&.enabled": {
      backgroundColor: "#f44336",
      color: "white",
      "&:hover": {
        backgroundColor: "#d32f2f",
      },
    },
    "&.disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
  sectionSaveButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#45a049",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
}));

const BetLimitsSection = ({
  operatorDetails,
  addBetLimit,
  removeAll,
  onRemoveBetLimit,
  onBetLimitChange,
  canEditMargins,
  onSave,
  loading,
}) => {
  const styles = useStyles();
  const isLocked = operatorDetails?.operatorMargins?.EXTRA_DATA?.isMarginEditingAllowed;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        backgroundColor: isLocked ? '#ffffff' : '#f5f5f5',
        border: isLocked ? '1px solid #4caf50' : '1px solid #e0e0e0',
        borderTop: isLocked ? '4px solid #4caf50' : '4px solid #ff9800',
        opacity: isLocked ? 1 : 0.7,
        transition: 'all 0.3s ease',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderBottom: isLocked ? '8px solid #4caf50' : '8px solid #ff9800',
        }
      }}
    >
      <Box mb={2}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography
            variant="h6"
            sx={{
              color: isLocked ? '#2e7d32' : '#757575',
              fontWeight: 'bold'
            }}
          >
            💰 Bet Limits Configuration
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 3,
              backgroundColor: isLocked ? '#e8f5e8' : '#fff3e0',
              border: isLocked ? '1px solid #4caf50' : '1px solid #ff9800',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isLocked ? '#2e7d32' : '#f57c00',
                fontWeight: 'bold',
                fontSize: '11px'
              }}
            >
              {isLocked ? 'EDITABLE' : 'LOCKED'}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {isLocked
            ? 'Configure betting limits for different currencies'
            : 'Enable editing mode above to modify betting limits'
          }
        </Typography>
      </Box>

      <Box className={styles.betLimitsButtonContainer}>
        <CustomButton
          onClick={addBetLimit}
          className={`${styles.addBetLimitButton} ${isLocked && canEditMargins ? "enabled" : "disabled"
            }`}
          disabled={!isLocked || !canEditMargins}
        >
          Add Bet Limit
        </CustomButton>
        <CustomButton
          onClick={removeAll}
          startIcon={<DeleteOutlineRounded />}
          className={`${styles.removeAllButton} ${isLocked && canEditMargins ? "enabled" : "disabled"
            }`}
          disabled={!isLocked || !canEditMargins}
        >
          Remove All
        </CustomButton>
      </Box>

      <Box className={styles.betLimitItemsContainer}>
        {operatorDetails.betLimits?.map((el, index) => {
          const isDisabled = !isLocked || !canEditMargins;
          return (
            <Paper
              key={index}
              elevation={0}
              className={`${styles.betLimitItemPaper} ${isDisabled ? "disabled" : "enabled"
                }`}
            >
              <Box className={styles.betLimitItemFields}>
                <Box className={styles.field}>
                  <SimpleSelectInput
                    label="Currency"
                    name="currency"
                    value={el.currency}
                    options={sessionHelperInstance.getCurrencyOptions()}
                    onChange={(e) => onBetLimitChange(e, index)}
                    disabled={isDisabled}
                  />
                </Box>
                <Box className={styles.smallField}>
                  <SimpleTextInput
                    type="number"
                    label="Minimum Bet"
                    name="minBet"
                    value={el.minBet}
                    onChange={(e) => onBetLimitChange(e, index)}
                    disabled={isDisabled}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
                <Box className={styles.smallField}>
                  <SimpleTextInput
                    type="number"
                    label="Maximum Bet"
                    name="maxBet"
                    value={el.maxBet}
                    onChange={(e) => onBetLimitChange(e, index)}
                    disabled={isDisabled}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
                <Box className={styles.smallField}>
                  <SimpleTextInput
                    type="number"
                    label="Maximum Payout"
                    name="maxPayOut"
                    value={el.maxPayOut}
                    onChange={(e) => onBetLimitChange(e, index)}
                    disabled={isDisabled}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
                <IconButton
                  onClick={() => onRemoveBetLimit(index)}
                  disabled={isDisabled}
                  className={`${styles.removeButton} ${isDisabled ? "disabled" : "enabled"
                    }`}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Section Save Button */}
      {isLocked && canEditMargins && onSave && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading}
            startIcon={<Save />}
            className={styles.sectionSaveButton}
          >
            {loading ? "Saving..." : "Save Bet Limits"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default BetLimitsSection;